import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "Bode's Galaxy",
    subtitle: "Messier 81 (M81) or NGC 3031",
    intro: "Discovered in 1774, it is a prominent, nearby galaxy with a 70-million-solar-mass central black hole and active star-forming regions in its arms, making it a popular target for observation.",
    keyDetails: "Key Details About M81:",
    location: "Location & Visibility",
    locationDesc: "Situated in the constellation Ursa Major, M81 is one of the brightest galaxies in the night sky (magnitude 6.9). It is visible through binoculars or small telescopes, often appearing alongside its neighbor, the Cigar Galaxy (M82).",
    structure: "Structure",
    structureDesc: "As a \"grand design\" spiral, its arms are well-defined and wind all the way into the nucleus. It has a large, bright central bulge containing older, redder stars.",
    dimensions: "Dimensions & Composition",
    dimensionsDesc: "The galaxy has a mean radius of roughly 45,000 light-years. It contains a massive 70 million solar mass black hole at its center.",
    proximity: "Proximity & Environment",
    proximityDesc: "M81 is the largest member of the M81 Group, a collection of roughly 30 galaxies. It is relatively close to the Milky Way and interacts with its neighbors, including a past close pass with M82 and NGC 3077.",
    observation: "Observation Tips",
    observationDesc: "The best time to observe M81 is in April. It is a popular target for amateur astrophotography due to its brightness and distinct structure, frequently imaged in tandem with M82.",
    note: "The galaxy, also known as NGC 3031, is considered a premier, easily spotted celestial object in the northern night sky."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Bodes Galakse",
    subtitle: "Messier 81 (M81) eller NGC 3031",
    intro: "Oppdaget i 1774, er det en fremtredende, nærliggende galakse med et sentralt svart hull på 70 millioner solmasser og aktive stjerneformende regioner i armene, noe som gjør den til et populært mål for observasjon.",
    keyDetails: "Nøkkeldetaljer om M81:",
    location: "Plassering og synlighet",
    locationDesc: "Plassert i stjernebildet Ursa Major, er M81 en av de lyseste galaksene på nattehimmelen (magnitude 6.9). Den er synlig gjennom kikkert eller små teleskoper, og vises ofte sammen med naboen, Sigargalaksen (M82).",
    structure: "Struktur",
    structureDesc: "Som en \"grand design\" spiral er armene veldefinerte og snor seg helt inn i kjernen. Den har en stor, lys sentral bule som inneholder eldre, rødere stjerner.",
    dimensions: "Dimensjoner og sammensetning",
    dimensionsDesc: "Galaksen har en gjennomsnittsradius på omtrent 45 000 lysår. Den inneholder et massivt svart hull på 70 millioner solmasser i sentrum.",
    proximity: "Nærhet og miljø",
    proximityDesc: "M81 er det største medlemmet av M81-gruppen, en samling av omtrent 30 galakser. Den er relativt nær Melkeveien og samhandler med naboene, inkludert en tidligere nær passering med M82 og NGC 3077.",
    observation: "Observasjonstips",
    observationDesc: "Den beste tiden å observere M81 er i april. Den er et populært mål for amatørastrofotografering på grunn av dens lysstyrke og distinkte struktur, ofte avbildet sammen med M82.",
    note: "Galaksen, også kjent som NGC 3031, anses som et førsteklasses, lett synlig himmelsk objekt på den nordlige nattehimmelen."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Galaktyka Bodego",
    subtitle: "Messier 81 (M81) lub NGC 3031",
    intro: "Odkryta w 1774 roku, jest to widoczna, bliska galaktyka z centralną czarną dziurą o masie 70 milionów mas Słońca i aktywnymi regionami gwiazdotwórczymi w ramionach, co czyni ją popularnym celem obserwacji.",
    keyDetails: "Kluczowe szczegóły o M81:",
    location: "Lokalizacja i widoczność",
    locationDesc: "Położona w gwiazdozbiorze Wielkiej Niedźwiedzicy, M81 jest jedną z najjaśniejszych galaktyk na nocnym niebie (magnitudo 6.9). Jest widoczna przez lornetkę lub małe teleskopy, często pojawiając się obok swojego sąsiada, Galaktyki Cygaro (M82).",
    structure: "Struktura",
    structureDesc: "Jako spirala \"wielkiego projektu\", jej ramiona są dobrze zdefiniowane i spiralnie wchodzą aż do jądra. Ma dużą, jasną centralną wypukłość zawierającą starsze, bardziej czerwone gwiazdy.",
    dimensions: "Wymiary i skład",
    dimensionsDesc: "Galaktyka ma średni promień około 45 000 lat świetlnych. Zawiera masywną czarną dziurę o masie 70 milionów mas Słońca w centrum.",
    proximity: "Bliskość i środowisko",
    proximityDesc: "M81 jest największym członkiem Grupy M81, kolekcji około 30 galaktyk. Jest stosunkowo blisko Drogi Mlecznej i wchodzi w interakcje z sąsiadami, w tym z przeszłym bliskim przelotem z M82 i NGC 3077.",
    observation: "Wskazówki obserwacyjne",
    observationDesc: "Najlepszy czas na obserwację M81 to kwiecień. Jest popularnym celem dla amatorskiej astrofotografii ze względu na jej jasność i wyraźną strukturę, często fotografowana razem z M82.",
    note: "Galaktyka, znana również jako NGC 3031, jest uważana za doskonały, łatwo zauważalny obiekt niebieski na północnym nocnym niebie."
  }
};

export default function BodeDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-purple-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="bode-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="bode-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Intro */}
            <p className="text-white/90 text-lg leading-relaxed">
              {t.intro}
            </p>

            {/* Key Details */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.keyDetails}
              </h2>

              <div className="space-y-6 text-white/80 leading-relaxed">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.location}
                  </h3>
                  <p>{t.locationDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.structure}
                  </h3>
                  <p>{t.structureDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.dimensions}
                  </h3>
                  <p>{t.dimensionsDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.proximity}
                  </h3>
                  <p>{t.proximityDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.observation}
                  </h3>
                  <p>{t.observationDesc}</p>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="pt-6 border-t border-white/10">
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-6">
                <p className="text-white/90 leading-relaxed">
                  {t.note}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
